import { Translation, TranslocoEvents, TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, of, switchMap, throwError } from 'rxjs';

export abstract class TranslocoUtils {
  static readonly DEFAULT_LOCALE = 'en_GB';
  static readonly DEFAULT_LOCALE_DECIMAL_FORMAT = 'en-GB';
  static readonly DEFAULT_LANG = 'en';
  static readonly LANGS = ['en', 'hu', 'fr', 'it', 'pt', 'es', 'ja', 'de'] as const;
  static readonly PARAM_TO_LOCALE_MAP = {
    '^en_US$': 'en_US',
    '^en.*$': 'en_GB',
    '^hu.*$': 'hu_HU',
    '^fr.*$': 'fr_FR',
    '^it.*$': 'it_IT',
    '^pt_BR$': 'pt_BR',
    '^pt.*$': 'pt_PT',
    '^es_AR$': 'es_AR',
    '^es.*$': 'es_ES',
    '^ja.*$': 'ja_JP',
    '^de.*$': 'de_DE',
  };

  static readonly LOCALE_TO_LANG_MAP = {
    '^en.*$': 'en',
    '^fr.*$': 'fr',
    // Not supported languages for now
    // '^it.*$': 'it',
    // '^hu.*$': 'hu',
    // '^de.*$': 'de',
    // '^ja.*$': 'ja',
    // '^pt.*$': 'pt',
    // '^es.*$': 'es',
  };

  static readonly LANG_TO_LOCALE_MAPPING = {
    en: 'en-GB',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
    hu: 'hu-HU',
    pt: 'pt-PT',
    it: 'it-IT',
    ja: 'ja-JP',
  };

  static loader(importFunc: (lang: string) => Promise<Translation>): Record<string, () => Promise<Translation>> {
    return TranslocoUtils.LANGS.reduce((acc: { [key: string]: () => Promise<Translation> }, lang: string) => {
      acc[lang] = (): Promise<Translation> => importFunc(lang);
      return acc;
    }, {});
  }

  static mapParamToLocale(locale: string, navigatorLocale?: string | undefined): string {
    let localeMapped: undefined | string;
    Object.entries(TranslocoUtils.PARAM_TO_LOCALE_MAP).forEach(([pattern, validLocal]) => {
      if (!localeMapped && new RegExp(pattern).test(locale)) {
        localeMapped = validLocal;
      }
    });
    if (localeMapped) {
      localeMapped = localeMapped.replace('_', '-');
    }
    return localeMapped ?? navigatorLocale ?? TranslocoUtils.DEFAULT_LOCALE;
  }

  static mapLocaleToLang(locale: string): string {
    let langMapped: undefined | string;
    Object.entries(TranslocoUtils.LOCALE_TO_LANG_MAP).forEach(([pattern, validLang]) => {
      if (!langMapped && new RegExp(pattern).test(locale)) {
        langMapped = validLang;
      }
    });
    return langMapped ?? TranslocoUtils.DEFAULT_LANG;
  }

  static mapLangToLocale(locale: string): string {
    let langMapped: undefined | string;
    Object.entries(TranslocoUtils.PARAM_TO_LOCALE_MAP).forEach(([pattern, validLang]) => {
      if (!langMapped && new RegExp(pattern).test(locale)) {
        langMapped = validLang;
      }
    });
    if (langMapped) {
      langMapped = langMapped.replace('_', '-');
    }
    return langMapped ?? TranslocoUtils.DEFAULT_LOCALE_DECIMAL_FORMAT;
  }

  /**
   * Method that listens to transloco events and emits when the given scope has loaded or failed
   */
  static onScopeLoaded(translocoService: TranslocoService, targetScope: string): Observable<string> {
    if (translocoService.getTranslation(targetScope) && Object.keys(translocoService.getTranslation(targetScope)).length > 0) {
      return of(targetScope);
    }

    return translocoService.events$.pipe(
      switchMap((event: TranslocoEvents) => {
        if (!event || event.payload.scope !== targetScope) {
          return EMPTY;
        }
        if (event.type === 'translationLoadFailure') {
          return throwError(() => new Error(`Translation load failed for scope ${targetScope}`));
        }
        return of(event.payload.scope);
      })
    );
  }
}
